import { isDefined, isNil, isNonEmptyString } from "@clipboard-health/util-ts";
import { datadogRum } from "@datadog/browser-rum";
import { addMilliseconds, differenceInMilliseconds, isAfter, isValid } from "date-fns";

// Import from the concrete analytics submodules (not the barrel) so test
// files that partially mock the "../lib/analytics" index don't have to know
// about this module's dependencies.
import { APP_V3_APP_EVENTS } from "../lib/analytics/events";
import { logEvent } from "../lib/analytics/log";
import { resolveLoginFlowCohort } from "./loginFlowTelemetry";

export const LOGIN_JOURNEY_THRESHOLD_MS = 15 * 60 * 1000;

const LOGIN_JOURNEY_STORAGE_KEY = "login_journey_telemetry";
const UNKNOWN_LOGIN_METHOD = "unknown";

/**
 * A login journey opens at the user's first attempt initiation (code/link
 * requested, password/passkey/SSO started) and completes at the next terminal
 * login success. `completed(withinThreshold) / started` is the exact
 * eventual-success rate the rollout dashboards previously approximated with
 * per-device cardinality ratios; abandonment is derived as
 * `started − completed`. The record lives in localStorage so it survives
 * magic-link/SSO full-page redirects in the same browser. All storage access
 * is best-effort — telemetry must never block or delay login.
 */
interface StoredLoginJourney {
  journeyId: string;
  startedAt: number;
  attempts: number;
  firstMethod: string;
  // The submitted login email, normalized (trimmed, lowercased). Absent when
  // the initiation carries no email (e.g. usernameless passkey autofill).
  email?: string;
}

interface RecordLoginAttemptInitiatedParams {
  method: string;
  email?: string;
}

/**
 * Opens a journey at an attempt-initiation telemetry site (OTP/magic-link
 * requested, password/passkey/SSO started). Repeat initiations (resend,
 * switching method) while a journey is active are no-ops on the clock — the
 * `startedAt`/`journeyId` keep running from the initial attempt — except that
 * a corrected email (typo fix) updates the stored `email`.
 */
export function recordLoginAttemptInitiated({
  method,
  email,
}: RecordLoginAttemptInitiatedParams): void {
  try {
    const normalizedEmail = normalizeLoginEmail(email);
    const journey = readActiveJourney();
    if (isDefined(journey)) {
      // Repeat initiation: keep the clock, but track a corrected email.
      if (isDefined(normalizedEmail) && normalizedEmail !== journey.email) {
        storeJourney({ ...journey, email: normalizedEmail });
      }
      return;
    }

    openJourney({ firstMethod: method, attempts: 0, email: normalizedEmail });
  } catch {
    // Journey telemetry is best-effort and must never block or delay login.
  }
}

/**
 * Counts a terminal login failure toward the active journey. When no journey
 * is active (missed initiation path, or the record expired), opens one
 * defensively so the failure is still tracked.
 */
export function recordTerminalLoginFailure(): void {
  try {
    const journey = readActiveJourney();
    if (isDefined(journey)) {
      storeJourney({ ...journey, attempts: journey.attempts + 1 });
      return;
    }

    // Fall back to the RUM global `identityProvider` (set at each flow's
    // entry) as the first method when a failure opens a journey defensively.
    openJourney({
      firstMethod: readRumGlobalContextProperty("identityProvider") ?? UNKNOWN_LOGIN_METHOD,
      attempts: 1,
    });
  } catch {
    // Journey telemetry is best-effort and must never block or delay login.
  }
}

/**
 * Completes the journey on terminal login success. An over-threshold journey
 * still completes (with `withinThreshold: false`) so slow-but-eventual
 * successes stay measurable. A success with no stored journey is an
 * instrumentation gap and is emitted with `untrackedStart: true` so gaps are
 * monitorable without polluting the threshold rate.
 */
export function recordTerminalLoginSuccess(): void {
  try {
    const journey = readStoredJourney();
    removeStoredJourney();

    if (isNil(journey)) {
      logEvent(APP_V3_APP_EVENTS.LOGIN_JOURNEY_COMPLETED, { untrackedStart: true });
      return;
    }

    const timeToSuccessMs = differenceInMilliseconds(new Date(), new Date(journey.startedAt));
    logEvent(APP_V3_APP_EVENTS.LOGIN_JOURNEY_COMPLETED, {
      journeyId: journey.journeyId,
      timeToSuccessMs,
      // Stored `attempts` counts preceding terminal failures; +1 includes this
      // successful attempt, so `attempts` is the total number of login attempts
      // (a first-try success reports 1, not 0).
      attempts: journey.attempts + 1,
      firstMethod: journey.firstMethod,
      withinThreshold: timeToSuccessMs <= LOGIN_JOURNEY_THRESHOLD_MS,
    });
  } catch {
    // Journey telemetry is best-effort and must never block or delay login.
  }
}

interface ActiveLoginJourney {
  journeyId: string;
  email?: string;
}

/**
 * Returns the non-expired stored journey's id and attempted email, for
 * stamping `@context.loginJourneyId` / `@context.loginEmail` on every RUM
 * event while a journey is in flight. Read-only: expiry/corruption cleanup
 * happens at the next tracker call.
 */
export function getActiveLoginJourney(): ActiveLoginJourney | undefined {
  const journey = readStoredJourney({ cleanup: false });
  if (isNil(journey) || isJourneyExpired(journey)) {
    return undefined;
  }

  return { journeyId: journey.journeyId, email: journey.email };
}

function openJourney({
  firstMethod,
  attempts,
  email,
}: {
  firstMethod: string;
  attempts: number;
  email?: string;
}): void {
  // An undefined `email` is dropped by JSON.stringify on write, and
  // `isStoredLoginJourney` treats it as optional on read.
  const journey: StoredLoginJourney = {
    journeyId: generateJourneyId(),
    startedAt: Date.now(),
    attempts,
    firstMethod,
    email,
  };
  storeJourney(journey);
  logEvent(APP_V3_APP_EVENTS.LOGIN_JOURNEY_STARTED, {
    journeyId: journey.journeyId,
    firstMethod,
    loginFlow: getLoginFlow(),
  });
}

/** Reads the stored journey, lazily discarding an expired record. */
function readActiveJourney(): StoredLoginJourney | undefined {
  const journey = readStoredJourney();
  if (isNil(journey)) {
    return undefined;
  }

  if (isJourneyExpired(journey)) {
    removeStoredJourney();
    return undefined;
  }

  return journey;
}

function isJourneyExpired(journey: StoredLoginJourney): boolean {
  const startedAt = new Date(journey.startedAt);
  if (!isValid(startedAt)) {
    return true;
  }

  // Complement of the completion check (`elapsed <= threshold`), so a journey
  // is either within threshold or expired, never both.
  return isAfter(new Date(), addMilliseconds(startedAt, LOGIN_JOURNEY_THRESHOLD_MS));
}

/**
 * Reads and validates the stored journey. By default a corrupt/unparseable
 * record is discarded; pass `{ cleanup: false }` for the RUM hot path, which
 * must never write to localStorage (expiry/corruption cleanup then happens at
 * the next tracker call).
 */
function readStoredJourney(
  { cleanup }: { cleanup: boolean } = { cleanup: true }
): StoredLoginJourney | undefined {
  let storedValue: string | undefined;
  try {
    storedValue = localStorage.getItem(LOGIN_JOURNEY_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }

  if (isNil(storedValue)) {
    return undefined;
  }

  try {
    const parsedValue: unknown = JSON.parse(storedValue);
    if (isStoredLoginJourney(parsedValue)) {
      return parsedValue;
    }
  } catch {
    // Fall through to discard the corrupt record.
  }

  if (cleanup) {
    removeStoredJourney();
  }

  return undefined;
}

function isStoredLoginJourney(value: unknown): value is StoredLoginJourney {
  if (typeof value !== "object" || isNil(value)) {
    return false;
  }

  const candidate = value as {
    journeyId?: unknown;
    startedAt?: unknown;
    attempts?: unknown;
    firstMethod?: unknown;
    email?: unknown;
  };
  return (
    isNonEmptyString(candidate.journeyId) &&
    typeof candidate.startedAt === "number" &&
    Number.isFinite(candidate.startedAt) &&
    typeof candidate.attempts === "number" &&
    typeof candidate.firstMethod === "string" &&
    (candidate.email === undefined || typeof candidate.email === "string")
  );
}

/** Normalizes a submitted login email for use as a grouping key. */
function normalizeLoginEmail(email: string | undefined): string | undefined {
  if (isNil(email)) {
    return undefined;
  }

  const normalized = email.trim().toLowerCase();
  return isNonEmptyString(normalized) ? normalized : undefined;
}

function generateJourneyId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to the Math.random-based id.
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replaceAll(/[xy]/g, (char) => {
    const random = Math.trunc(Math.random() * 16);
    const value = char === "x" ? random : 8 + (random % 4);
    return value.toString(16);
  });
}

function getLoginFlow(): string | undefined {
  return resolveLoginFlowCohort(readRumGlobalContextProperty("loginFlow"));
}

function readRumGlobalContextProperty(key: string): string | undefined {
  try {
    const globalContext = datadogRum.getGlobalContext() as Record<string, unknown> | undefined;
    const value = globalContext?.[key];
    return typeof value === "string" ? value : undefined;
  } catch {
    return undefined;
  }
}

function storeJourney(journey: StoredLoginJourney): void {
  try {
    localStorage.setItem(LOGIN_JOURNEY_STORAGE_KEY, JSON.stringify(journey));
  } catch {
    // Journey telemetry is best-effort and must not block login.
  }
}

function removeStoredJourney(): void {
  try {
    localStorage.removeItem(LOGIN_JOURNEY_STORAGE_KEY);
  } catch {
    // Journey telemetry is best-effort and must not block login.
  }
}
